import React, { useState, useEffect, useRef } from 'react';
import './ConfiguracionSEO.css';
import baseURL from '../../url';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faShareAlt,
    faCode,
    faSave,
    faSync,
    faImage,
    faUpload,
    faCheckCircle,
    faCopy,
    faEye,
    faGlobe,
    faChartLine,
    faRobot,
    faPalette,
    faMobileAlt,
    faBrush,
    faTrash,
    faHeart,
    faLock
} from '@fortawesome/free-solid-svg-icons';
import { notificarCambioConfiguracion, aplicarConfiguracionEnDOM } from '../../useSiteConfig';

export default function ConfiguracionSEO() {
    const [activeTab, setActiveTab] = useState('colores'); // 'colores' | 'seo' | 'og' | 'code'
    const [socialPreviewMode, setSocialPreviewMode] = useState('whatsapp'); // 'whatsapp' | 'twitter'
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState(false);

    // Archivo de imagen Open Graph local
    const [imagenArchivo, setImagenArchivo] = useState(null);
    const [imagenPreviewLocal, setImagenPreviewLocal] = useState('');
    const fileInputRef = useRef(null);

    // Archivo de Logo del Sitio local (superior y footer)
    const [logoArchivo, setLogoArchivo] = useState(null);
    const [logoPreviewLocal, setLogoPreviewLocal] = useState('');
    const logoInputRef = useRef(null);

    // Archivo de Favicon local
    const [faviconArchivo, setFaviconArchivo] = useState(null);
    const [faviconPreviewLocal, setFaviconPreviewLocal] = useState('');
    const faviconInputRef = useRef(null);

    // Archivo de Fondo del Footer local
    const [footerBgArchivo, setFooterBgArchivo] = useState(null);
    const [footerBgPreviewLocal, setFooterBgPreviewLocal] = useState('');
    const footerBgInputRef = useRef(null);

    // Estado del formulario
    const [form, setForm] = useState({
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        meta_author: 'DXN Tingo María',
        canonical_url: 'https://dxntingomaria.com/',
        site_name: 'DXN Tingo María',
        google_verification: '',
        google_analytics_id: '',
        robots_index: 'index, follow',
        og_title: '',
        og_description: '',
        og_image: '',
        og_type: 'website',
        og_locale: 'es_PE',
        twitter_card: 'summary_large_image',
        twitter_site: '@dxntingomaria',
        logo: '',
        favicon: '',
        color_menu_movil: '#3F1D76',
        color_footer: '#3F1D76',
        footer_bg_image: '',
        footer_bg_opacity: '0.70'
    });

    useEffect(() => {
        cargarConfiguracion();
    }, []);

    const cargarConfiguracion = () => {
        setLoading(true);
        fetch(`${baseURL}/configuracionGet.php`)
            .then(res => res.json())
            .then(data => {
                if (data && data.config) {
                    setForm(prev => ({
                        ...prev,
                        ...data.config
                    }));
                    aplicarConfiguracionEnDOM(data.config);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error('Error al cargar configuración:', err);
                // Si falla el endpoint, cargar desde el JSON público directamente
                fetch('/configuracion_seo.json')
                    .then(res => res.json())
                    .then(jsonData => {
                        setForm(prev => ({ ...prev, ...jsonData }));
                        aplicarConfiguracionEnDOM(jsonData);
                    })
                    .catch(() => {})
                    .finally(() => setLoading(false));
            });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectColor = (campo, color) => {
        setForm(prev => ({ ...prev, [campo]: color }));
    };

    // Manejo de imagen Open Graph
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImagenArchivo(file);
            const previewUrl = URL.createObjectURL(file);
            setImagenPreviewLocal(previewUrl);
        }
    };

    const handleQuitarImagen = () => {
        setImagenArchivo(null);
        setImagenPreviewLocal('');
        setForm(prev => ({ ...prev, og_image: '' }));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Manejo de Logo del Sitio (superior y footer)
    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoArchivo(file);
            const previewUrl = URL.createObjectURL(file);
            setLogoPreviewLocal(previewUrl);
        }
    };

    const handleQuitarLogo = () => {
        setLogoArchivo(null);
        setLogoPreviewLocal('');
        setForm(prev => ({ ...prev, logo: '' }));
        if (logoInputRef.current) {
            logoInputRef.current.value = '';
        }
    };

    // Manejo de Favicon
    const handleFaviconChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFaviconArchivo(file);
            const previewUrl = URL.createObjectURL(file);
            setFaviconPreviewLocal(previewUrl);
        }
    };

    const handleQuitarFavicon = () => {
        setFaviconArchivo(null);
        setFaviconPreviewLocal('');
        setForm(prev => ({ ...prev, favicon: '' }));
        if (faviconInputRef.current) {
            faviconInputRef.current.value = '';
        }
    };

    // Manejo de Fondo del Footer
    const handleFooterBgChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFooterBgArchivo(file);
            const previewUrl = URL.createObjectURL(file);
            setFooterBgPreviewLocal(previewUrl);
        }
    };

    const handleQuitarFooterBg = () => {
        setFooterBgArchivo(null);
        setFooterBgPreviewLocal('');
        setForm(prev => ({ ...prev, footer_bg_image: '' }));
        if (footerBgInputRef.current) {
            footerBgInputRef.current.value = '';
        }
    };

    const handleCopiarDeSEO = () => {
        setForm(prev => ({
            ...prev,
            og_title: prev.meta_title,
            og_description: prev.meta_description
        }));
        Swal.fire({
            icon: 'info',
            title: 'Campos copiados',
            text: 'Se han sincronizado el título y la descripción de SEO para Open Graph.',
            timer: 1500,
            showConfirmButton: false
        });
    };

    const agregarKeyword = (palabra) => {
        const keywordsActuales = form.meta_keywords ? form.meta_keywords.split(',').map(k => k.trim()) : [];
        if (!keywordsActuales.includes(palabra)) {
            const nuevas = [...keywordsActuales, palabra].filter(Boolean).join(', ');
            setForm(prev => ({ ...prev, meta_keywords: nuevas }));
        }
    };

    const handleGuardar = async (e) => {
        if (e) e.preventDefault();

        if (!form.meta_title.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo Requerido',
                text: 'El título de la página (Meta Title) es indispensable para el posicionamiento en Google.'
            });
            return;
        }

        setGuardando(true);

        const formData = new FormData();
        Object.keys(form).forEach(key => {
            formData.append(key, form[key] || '');
        });

        if (imagenArchivo) {
            formData.append('og_image_file', imagenArchivo);
        }
        if (logoArchivo) {
            formData.append('logo_file', logoArchivo);
        }
        if (faviconArchivo) {
            formData.append('favicon_file', faviconArchivo);
        }
        if (footerBgArchivo) {
            formData.append('footer_bg_image_file', footerBgArchivo);
        }

        try {
            const res = await fetch(`${baseURL}/configuracionPost.php`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const data = await res.json();

            if (res.ok && data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: '¡Configuración Guardada!',
                    text: 'Los cambios de Colores, Logos, Favicon, Fondo del Footer, SEO y Open Graph se han guardado y aplicado correctamente.',
                    confirmButtonColor: '#3F1D76'
                });

                if (data.config) {
                    setForm(prev => ({ ...prev, ...data.config }));
                    setImagenArchivo(null);
                    setImagenPreviewLocal('');
                    setLogoArchivo(null);
                    setLogoPreviewLocal('');
                    setFaviconArchivo(null);
                    setFaviconPreviewLocal('');
                    setFooterBgArchivo(null);
                    setFooterBgPreviewLocal('');
                    notificarCambioConfiguracion(data.config);
                    aplicarConfiguracionEnDOM(data.config);
                }
            } else {
                throw new Error(data.error || 'Error al guardar la configuración');
            }
        } catch (error) {
            console.error('Error al guardar:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'No se pudo conectar con el servidor para guardar los cambios.'
            });
        } finally {
            setGuardando(false);
        }
    };

    // Cálculos de longitud de caracteres
    const titleLen = form.meta_title?.length || 0;
    const descLen = form.meta_description?.length || 0;
    const ogTitleLen = form.og_title?.length || 0;
    const ogDescLen = form.og_description?.length || 0;

    const imagenMostrada = imagenPreviewLocal || form.og_image || '/logo.png';

    // Generar bloque HTML para Tab 3
    const codigoGenerado = `<!-- ============================================== -->
<!-- CONFIGURACIÓN SEO Y GOOGLE (DXN TINGO MARÍA)     -->
<!-- ============================================== -->
<title>${form.meta_title || 'DXN Tingo María'}</title>
<meta name="description" content="${form.meta_description || ''}" />
<meta name="keywords" content="${form.meta_keywords || ''}" />
<meta name="author" content="${form.meta_author || 'DXN Tingo María'}" />
<meta name="robots" content="${form.robots_index || 'index, follow'}" />
<link rel="canonical" href="${form.canonical_url || 'https://dxntingomaria.com/'}" />

${form.google_verification ? `<!-- Google Search Console -->
<meta name="google-site-verification" content="${form.google_verification}" />\n` : ''}${form.google_analytics_id ? `<!-- Google Analytics 4 (GA4) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=${form.google_analytics_id}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${form.google_analytics_id}');
</script>\n` : ''}<!-- ============================================== -->
<!-- OPEN GRAPH & REDES SOCIALES (FACEBOOK, WHATSAPP)-->
<!-- ============================================== -->
<meta property="og:type" content="${form.og_type || 'website'}" />
<meta property="og:site_name" content="${form.site_name || 'DXN Tingo María'}" />
<meta property="og:locale" content="${form.og_locale || 'es_PE'}" />
<meta property="og:title" content="${form.og_title || form.meta_title || ''}" />
<meta property="og:description" content="${form.og_description || form.meta_description || ''}" />
<meta property="og:image" content="${form.og_image || ''}" />
<meta property="og:url" content="${form.canonical_url || 'https://dxntingomaria.com/'}" />

<!-- ============================================== -->
<!-- TWITTER / X CARDS                              -->
<!-- ============================================== -->
<meta name="twitter:card" content="${form.twitter_card || 'summary_large_image'}" />
<meta name="twitter:title" content="${form.og_title || form.meta_title || ''}" />
<meta name="twitter:description" content="${form.og_description || form.meta_description || ''}" />
<meta name="twitter:image" content="${form.og_image || ''}" />
${form.twitter_site ? `<meta name="twitter:site" content="${form.twitter_site}" />` : ''}`;

    const copiarCodigoPortapapeles = () => {
        navigator.clipboard.writeText(codigoGenerado);
        Swal.fire({
            icon: 'success',
            title: '¡Código Copiado!',
            text: 'Las etiquetas meta fueron copiadas al portapapeles.',
            timer: 1500,
            showConfirmButton: false
        });
    };

    return (
        <div className="seoConfigContainer">
            {/* Header del Módulo */}
            <div className="seoConfigHeader">
                <div className="seoConfigHeaderLeft">
                    <div className="seoIconBadge">
                        <FontAwesomeIcon icon={faPalette} />
                    </div>
                    <div>
                        <h2>Configuración de Identidad, Colores y SEO</h2>
                        <p>
                            Personaliza el logotipo oficial para la barra superior y footer, carga el favicon, ajusta los colores del menú móvil y footer, y optimiza el posicionamiento en Google.
                        </p>
                    </div>
                </div>

                <div className="seoHeaderActions">
                    <button
                        type="button"
                        onClick={cargarConfiguracion}
                        className="btnSecondarySeo"
                        title="Recargar configuración"
                        disabled={loading || guardando}
                    >
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                        <span>Recargar</span>
                    </button>
                    <button
                        type="button"
                        onClick={handleGuardar}
                        className="btnPrimarySeo"
                        disabled={guardando}
                    >
                        <FontAwesomeIcon icon={faSave} />
                        <span>{guardando ? 'Guardando...' : 'Guardar Cambios'}</span>
                    </button>
                </div>
            </div>

            {/* Pestañas de Navegación del Módulo */}
            <div className="seoTabsBar">
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'colores' ? 'active' : ''}`}
                    onClick={() => setActiveTab('colores')}
                >
                    <FontAwesomeIcon icon={faPalette} />
                    <span>Colores e Identidad (Logos & Colores)</span>
                </button>
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'seo' ? 'active' : ''}`}
                    onClick={() => setActiveTab('seo')}
                >
                    <FontAwesomeIcon icon={faSearch} />
                    <span>Motores de Búsqueda y Google (SEO)</span>
                </button>
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'og' ? 'active' : ''}`}
                    onClick={() => setActiveTab('og')}
                >
                    <FontAwesomeIcon icon={faShareAlt} />
                    <span>Redes Sociales & Open Graph (OG Image)</span>
                </button>
                <button
                    type="button"
                    className={`seoTabBtn ${activeTab === 'code' ? 'active' : ''}`}
                    onClick={() => setActiveTab('code')}
                >
                    <FontAwesomeIcon icon={faCode} />
                    <span>Código HTML & Diagnóstico</span>
                </button>
            </div>

            {/* CONTENIDO DE PESTAÑAS */}
            <div className="seoTabContentWrapper">
                {/* ======================================================== */}
                {/* TAB 0: COLORES E IDENTIDAD (LOGOS, FAVICON Y COLORES)     */}
                {/* ======================================================== */}
                {activeTab === 'colores' && (
                    <div className="seoTabGrid">
                        {/* Columna Izquierda: Formulario de Identidad y Colores */}
                        <div className="seoFormCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faPalette} /> Identidad Visual y Paleta de Colores
                            </h3>

                            {/* 1. Logo del Sitio (Navbar Superior y Footer) */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faImage} style={{ color: '#8b5cf6' }} /> Logo del Sitio (Superior & Footer)
                                    </h4>
                                    <span className="identityBadgeTag">Navbar & Footer</span>
                                </div>
                                <p className="identityDescription">
                                    Carga la imagen de tu logotipo. Esta misma imagen se aplicará de forma automática en la barra superior (Navbar) y en el pie de página (Footer) de toda la web.
                                </p>

                                <div className="identityUploadFlex">
                                    <div className="identityPreviewBox">
                                        <img
                                            src={logoPreviewLocal || form.logo || '/logo.png'}
                                            alt="Logo del sitio"
                                            className="identityPreviewImg"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                    </div>
                                    <div className="identityUploadActions">
                                        <div className="identityUploadBtns">
                                            <input
                                                type="file"
                                                ref={logoInputRef}
                                                onChange={handleLogoChange}
                                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                                style={{ display: 'none' }}
                                            />
                                            <button
                                                type="button"
                                                className="btnUploadFile"
                                                onClick={() => logoInputRef.current && logoInputRef.current.click()}
                                            >
                                                <FontAwesomeIcon icon={faUpload} /> Seleccionar Logo
                                            </button>
                                            {(logoPreviewLocal || (form.logo && form.logo !== '/logo.png')) && (
                                                <button
                                                    type="button"
                                                    className="btnRemoveFile"
                                                    onClick={handleQuitarLogo}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} /> Restaurar Por Defecto
                                                </button>
                                            )}
                                        </div>
                                        <p className="identityFormatTip">
                                            Formatos recomendados: PNG con fondo transparente, SVG o WEBP. Tamaño sugerido: 200x200 px o superior.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Favicon (Pestaña del Navegador) */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faGlobe} style={{ color: '#0ea5e9' }} /> Favicon (Ícono del Navegador)
                                    </h4>
                                    <span className="identityBadgeTag">Pestaña Browser</span>
                                </div>
                                <p className="identityDescription">
                                    El favicon es el pequeño ícono que aparece en la pestaña del navegador junto al título de tu página web y en los marcadores de los clientes.
                                </p>

                                <div className="identityUploadFlex">
                                    <div className="identityPreviewBox faviconBox">
                                        <img
                                            src={faviconPreviewLocal || form.favicon || form.logo || '/logo.png'}
                                            alt="Favicon"
                                            className="identityPreviewImg"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                    </div>
                                    <div className="identityUploadActions">
                                        <div className="identityUploadBtns">
                                            <input
                                                type="file"
                                                ref={faviconInputRef}
                                                onChange={handleFaviconChange}
                                                accept=".ico,image/png,image/svg+xml,image/webp"
                                                style={{ display: 'none' }}
                                            />
                                            <button
                                                type="button"
                                                className="btnUploadFile"
                                                onClick={() => faviconInputRef.current && faviconInputRef.current.click()}
                                            >
                                                <FontAwesomeIcon icon={faUpload} /> Seleccionar Favicon
                                            </button>
                                            {(faviconPreviewLocal || (form.favicon && form.favicon !== '/logo.png')) && (
                                                <button
                                                    type="button"
                                                    className="btnRemoveFile"
                                                    onClick={handleQuitarFavicon}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} /> Restaurar Por Defecto
                                                </button>
                                            )}
                                        </div>
                                        <p className="identityFormatTip">
                                            Formatos compatibles: .ico, .png, .svg. Tamaño sugerido: 32x32 px o 64x64 px.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* 3. Color de la Barra de Menú (Modo Móvil), Modales y Botones */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#10b981' }} /> Color de la Barra de Menú (Modo Móvil), Modales y Botones
                                    </h4>
                                    <span className="identityBadgeTag">Móvil, Modales y Botones</span>
                                </div>
                                <p className="identityDescription">
                                    Selecciona el color principal para la barra de menú en modo móvil, las cabeceras de las ventanas modales (Favoritos y Carrito de compras) y los botones de toda la tienda.
                                </p>

                                <div className="colorPickerContainer">
                                    <div className="colorPickerControlRow">
                                        <div className="colorNativeWrapper" title="Haz clic para abrir el selector de color">
                                            <input
                                                type="color"
                                                name="color_menu_movil"
                                                value={form.color_menu_movil || '#3F1D76'}
                                                onChange={handleInputChange}
                                                className="colorNativeInput"
                                            />
                                            <div
                                                className="colorNativeSwatch"
                                                style={{ backgroundColor: form.color_menu_movil || '#3F1D76' }}
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            name="color_menu_movil"
                                            value={form.color_menu_movil || '#3F1D76'}
                                            onChange={handleInputChange}
                                            placeholder="#3F1D76"
                                            className="colorHexInput"
                                            maxLength={7}
                                        />
                                    </div>

                                    {/* Paleta de colores rápidos sugeridos */}
                                    <div className="colorPresetsGroup">
                                        <span className="colorPresetLabel">Colores sugeridos:</span>
                                        {[
                                            { label: 'DXN Púrpura Tradicional', hex: '#3F1D76' },
                                            { label: 'Morado Vibrante', hex: '#752FB5' },
                                            { label: 'Rojo Ganoderma', hex: '#C8102E' },
                                            { label: 'Azul Marino', hex: '#1E3A8A' },
                                            { label: 'Verde Salud Natural', hex: '#059669' },
                                            { label: 'Negro Carbón Elegante', hex: '#111827' },
                                            { label: 'Blanco Puro', hex: '#FFFFFF' }
                                        ].map(preset => (
                                            <button
                                                key={preset.hex}
                                                type="button"
                                                className={`colorPresetBtn ${form.color_menu_movil?.toLowerCase() === preset.hex.toLowerCase() ? 'active' : ''}`}
                                                style={{ backgroundColor: preset.hex }}
                                                title={preset.label}
                                                onClick={() => handleSelectColor('color_menu_movil', preset.hex)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* 4. Color del Footer (Pie de Página) */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faBrush} style={{ color: '#f59e0b' }} /> Color del Footer (Pie de Página)
                                    </h4>
                                    <span className="identityBadgeTag">Footer Global</span>
                                </div>
                                <p className="identityDescription">
                                    Selecciona el color de fondo para todo el pie de página de la tienda, donde se muestran las categorías, redes sociales y contacto.
                                </p>

                                <div className="colorPickerContainer">
                                    <div className="colorPickerControlRow">
                                        <div className="colorNativeWrapper" title="Haz clic para abrir el selector de color">
                                            <input
                                                type="color"
                                                name="color_footer"
                                                value={form.color_footer || '#3F1D76'}
                                                onChange={handleInputChange}
                                                className="colorNativeInput"
                                            />
                                            <div
                                                className="colorNativeSwatch"
                                                style={{ backgroundColor: form.color_footer || '#3F1D76' }}
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            name="color_footer"
                                            value={form.color_footer || '#3F1D76'}
                                            onChange={handleInputChange}
                                            placeholder="#3F1D76"
                                            className="colorHexInput"
                                            maxLength={7}
                                        />
                                    </div>

                                    {/* Paleta de colores rápidos sugeridos */}
                                    <div className="colorPresetsGroup">
                                        <span className="colorPresetLabel">Colores sugeridos:</span>
                                        {[
                                            { label: 'DXN Púrpura Tradicional', hex: '#3F1D76' },
                                            { label: 'Morado Vibrante', hex: '#752FB5' },
                                            { label: 'Rojo Ganoderma', hex: '#C8102E' },
                                            { label: 'Azul Petróleo Oscuro', hex: '#0F172A' },
                                            { label: 'Verde Bosque DXN', hex: '#064E3B' },
                                            { label: 'Carbón Grafito', hex: '#18181B' }
                                        ].map(preset => (
                                            <button
                                                key={preset.hex}
                                                type="button"
                                                className={`colorPresetBtn ${form.color_footer?.toLowerCase() === preset.hex.toLowerCase() ? 'active' : ''}`}
                                                style={{ backgroundColor: preset.hex }}
                                                title={preset.label}
                                                onClick={() => handleSelectColor('color_footer', preset.hex)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* 5. Imagen de Fondo del Pie de Página (Footer) */}
                            <div className="identityCardBlock">
                                <div className="identityCardHeader">
                                    <h4>
                                        <FontAwesomeIcon icon={faImage} style={{ color: '#8b5cf6' }} /> Imagen de Fondo del Pie de Página (Footer)
                                    </h4>
                                    <span className="identityBadgeTag">Fondo Decorativo</span>
                                </div>
                                <p className="identityDescription">
                                    Adjunta una imagen para colocarla como fondo del pie de página (Footer). Se mostrará combinada con el color seleccionado y se adaptará a lo ancho de toda la tienda.
                                </p>

                                <div className="fileUploadGroup">
                                    <input
                                        type="file"
                                        ref={footerBgInputRef}
                                        onChange={handleFooterBgChange}
                                        accept="image/png, image/jpeg, image/webp, image/svg+xml"
                                        style={{ display: 'none' }}
                                    />

                                    {(footerBgPreviewLocal || form.footer_bg_image) ? (
                                        <div className="filePreviewBox footerBgPreviewBox">
                                            <div className="footerBgImageThumbWrapper">
                                                <img
                                                    src={footerBgPreviewLocal || form.footer_bg_image}
                                                    alt="Fondo Footer"
                                                    className="footerBgImgPreview"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                            <div className="filePreviewActions">
                                                <button
                                                    type="button"
                                                    onClick={() => footerBgInputRef.current?.click()}
                                                    className="btnUploadFile"
                                                >
                                                    <FontAwesomeIcon icon={faUpload} /> Cambiar Imagen
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleQuitarFooterBg}
                                                    className="btnRemoveFile"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} /> Quitar Imagen
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className="fileDropZone"
                                            onClick={() => footerBgInputRef.current?.click()}
                                        >
                                            <FontAwesomeIcon icon={faImage} className="fileDropIcon" style={{ color: '#8b5cf6' }} />
                                            <span className="fileDropText">Haz clic para adjuntar imagen de fondo para el Footer</span>
                                            <span className="fileDropHint">PNG, JPG, WEBP • Recomendado: formato panorámico (ej: 1920 × 500 px)</span>
                                        </div>
                                    )}
                                </div>

                                {/* Controlador Interactivo de Nivel de Transparencia */}
                                <div className="transparencyControlBox">
                                    <div className="transparencyHeaderRow">
                                        <div className="transparencyTitleWrapper">
                                            <FontAwesomeIcon icon={faEye} style={{ color: '#8b5cf6' }} />
                                            <span className="transparencyLabel">Nivel de Transparencia de la Imagen</span>
                                        </div>
                                        <div className="transparencyBadgesRow">
                                            <span className="badgeTransparency">
                                                {Math.round((1 - parseFloat(form.footer_bg_opacity !== undefined ? form.footer_bg_opacity : 0.70)) * 100)}% Transparencia
                                            </span>
                                            <span className="badgeOpacity">
                                                {Math.round(parseFloat(form.footer_bg_opacity !== undefined ? form.footer_bg_opacity : 0.70) * 100)}% Visible
                                            </span>
                                        </div>
                                    </div>

                                    <div className="transparencySliderRow">
                                        <span className="sliderBoundText">0% (Nítido)</span>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="5"
                                            value={Math.round((1 - parseFloat(form.footer_bg_opacity !== undefined ? form.footer_bg_opacity : 0.70)) * 100)}
                                            onChange={(e) => {
                                                const trans = parseInt(e.target.value, 10);
                                                const opacity = ((100 - trans) / 100).toFixed(2);
                                                setForm(prev => ({ ...prev, footer_bg_opacity: opacity }));
                                            }}
                                            className="transparencySlider"
                                        />
                                        <span className="sliderBoundText">100% (Transparente)</span>
                                    </div>

                                    {/* Botones de Ajustes Rápidos */}
                                    <div className="transparencyPresetsRow">
                                        <span className="presetTitleText">Ajustes rápidos:</span>
                                        {[
                                            { label: '10%', trans: 10 },
                                            { label: '20%', trans: 20 },
                                            { label: '30% (Recomendado)', trans: 30 },
                                            { label: '50%', trans: 50 },
                                            { label: '70%', trans: 70 },
                                            { label: '85%', trans: 85 }
                                        ].map(preset => {
                                            const currentTrans = Math.round((1 - parseFloat(form.footer_bg_opacity !== undefined ? form.footer_bg_opacity : 0.70)) * 100);
                                            const isActive = currentTrans === preset.trans;
                                            return (
                                                <button
                                                    key={preset.trans}
                                                    type="button"
                                                    onClick={() => {
                                                        const opacity = ((100 - preset.trans) / 100).toFixed(2);
                                                        setForm(prev => ({ ...prev, footer_bg_opacity: opacity }));
                                                    }}
                                                    className={`btnTransparencyPreset ${isActive ? 'active' : ''}`}
                                                >
                                                    {preset.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Columna Derecha: Simuladores en Tiempo Real */}
                        <div className="seoPreviewCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faEye} /> Simuladores en Tiempo Real
                            </h3>
                            <p className="previewBoxIntro">
                                Previsualiza exactamente cómo se verá tu tienda con el nuevo logo, favicon y colores antes de guardar los cambios.
                            </p>

                            {/* Mockup 1: Pestaña del Navegador con Favicon */}
                            <div className="mockupSectionBlock">
                                <h5 className="mockupTitle">
                                    <FontAwesomeIcon icon={faGlobe} style={{ color: '#0ea5e9' }} /> 1. Pestaña del Navegador (Favicon)
                                </h5>
                                <div className="browserTabMockup">
                                    <div className="browserTabItem">
                                        <img
                                            src={faviconPreviewLocal || form.favicon || logoPreviewLocal || form.logo || '/logo.png'}
                                            alt="Favicon"
                                            className="browserTabFavicon"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                        <span className="browserTabLabel">
                                            {form.meta_title || 'DXN Tingo María - Salud y Bienestar'}
                                        </span>
                                        <span className="browserTabClose">✕</span>
                                    </div>
                                </div>
                                <div className="browserUrlBarMockup">
                                    <FontAwesomeIcon icon={faLock} className="browserUrlLock" />
                                    <span className="browserUrlText">
                                        {form.canonical_url || 'https://dxntingomaria.com'}
                                    </span>
                                </div>
                            </div>

                            {/* Mockup 2: Barra Móvil en Smartphone */}
                            <div className="mockupSectionBlock">
                                <h5 className="mockupTitle">
                                    <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#10b981' }} /> 2. Barra de Menú Móvil en Celulares
                                </h5>
                                <div className="mobileNavPhoneFrame">
                                    <div className="phoneTopNotch">
                                        <div className="phoneNotchPill"></div>
                                    </div>
                                    <div
                                        className="mobileNavPreviewBar"
                                        style={{ backgroundColor: form.color_menu_movil || '#3F1D76' }}
                                    >
                                        <img
                                            src={logoPreviewLocal || form.logo || '/logo.png'}
                                            alt="Logo Móvil"
                                            className="mobileNavLogoImg"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                        <div className="mobileNavActionIcons">
                                            <FontAwesomeIcon icon={faHeart} title="Favoritos" />
                                            <FontAwesomeIcon icon={faSearch} title="Buscar" />
                                            <div className="mobileNavHamburger" title="Menú">
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mobilePhoneContentDummy">
                                        <span className="dummyBannerText">
                                            {form.site_name || 'DXN Tingo María'}
                                        </span>
                                        <span className="dummyBannerSub">
                                            Distribuidor Oficial de Productos con Ganoderma
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Mockup 3: Footer de la Tienda */}
                            <div className="mockupSectionBlock">
                                <h5 className="mockupTitle">
                                    <FontAwesomeIcon icon={faBrush} style={{ color: '#f59e0b' }} /> 3. Pie de Página (Footer)
                                </h5>
                                <div
                                    className="footerMockupCard"
                                    style={{
                                        backgroundColor: form.color_footer || '#3F1D76',
                                        '--footer-mockup-bg': (footerBgPreviewLocal || form.footer_bg_image)
                                            ? `url("${footerBgPreviewLocal || form.footer_bg_image}")`
                                            : 'none',
                                        '--footer-mockup-opacity': form.footer_bg_opacity !== undefined
                                            ? form.footer_bg_opacity
                                            : '0.70'
                                    }}
                                >
                                    <div className="footerMockupTop">
                                        <img
                                            src={logoPreviewLocal || form.logo || '/logo.png'}
                                            alt="Logo Footer"
                                            className="footerMockupLogoImg"
                                            onError={(e) => { e.target.src = '/logo.png'; }}
                                        />
                                        <div>
                                            <h4 className="footerMockupBrandName">
                                                {form.site_name || 'DXN Tingo María'}
                                            </h4>
                                        </div>
                                    </div>
                                    <div className="footerMockupLinksRow">
                                        <span>Productos</span>
                                        <span>Quiénes Somos</span>
                                        <span>Afiliaciones</span>
                                        <span>Contacto</span>
                                    </div>
                                    <div className="footerMockupSocials">
                                        <div className="footerMockupSocialIcon"><i className="fa fa-facebook"></i></div>
                                        <div className="footerMockupSocialIcon"><i className="fa fa-instagram"></i></div>
                                        <div className="footerMockupSocialIcon"><i className="fa fa-whatsapp"></i></div>
                                    </div>
                                </div>
                            </div>

                            {/* Mockup 4: Ventanas Modales (Favoritos & Carrito) y Botones */}
                            <div className="mockupSectionBlock">
                                <h5 className="mockupTitle">
                                    <FontAwesomeIcon icon={faHeart} style={{ color: '#ec4899' }} /> 4. Ventanas Modales (Favoritos / Carrito) y Botones
                                </h5>
                                <div className="modalMockupCard">
                                    {/* Cabecera del Modal (idéntica a .deFLex) */}
                                    <div
                                        className="modalMockupHeader"
                                        style={{ backgroundColor: form.color_menu_movil || '#3F1D76' }}
                                    >
                                        <div className="modalMockupHeaderLeft">
                                            <span>←</span>
                                            <span>Favoritos / Carrito</span>
                                        </div>
                                        <span className="modalMockupBadge">Cerrar ✕</span>
                                    </div>
                                    {/* Contenido del Modal con producto y botones */}
                                    <div className="modalMockupBody">
                                        <div className="modalMockupItem">
                                            <div className="modalMockupItemImg"></div>
                                            <div className="modalMockupItemInfo">
                                                <span className="modalMockupItemTitle">Café Lingzhi 3 en 1</span>
                                                <span className="modalMockupItemPrice">S/ 65.00</span>
                                            </div>
                                            <div
                                                className="modalMockupItemQty"
                                                style={{ backgroundColor: form.color_menu_movil || '#3F1D76' }}
                                            >
                                                +
                                            </div>
                                        </div>
                                        {/* Botón de acción principal de la tienda */}
                                        <button
                                            type="button"
                                            className="modalMockupActionBtn"
                                            style={{ backgroundColor: form.color_menu_movil || '#3F1D76' }}
                                        >
                                            Pedir / Enviar por WhatsApp
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ======================================================== */}
                {/* TAB 1: MOTORES DE BÚSQUEDA Y GOOGLE (SEO)               */}
                {/* ======================================================== */}
                {activeTab === 'seo' && (
                    <div className="seoTabGrid">
                        {/* Columna Izquierda: Formulario SEO */}
                        <div className="seoFormCard">
                            <h3 className="sectionSubtitle">
                                <FontAwesomeIcon icon={faGlobe} /> Parámetros Principales de Búsqueda
                            </h3>

                            {/* Meta Title */}
                            <div className="seoFormGroup">
                                <div className="labelFlex">
                                    <label htmlFor="meta_title">Título para Buscadores (Meta Title) *</label>
                                    <span className={`charCount ${titleLen > 65 ? 'warning' : 'good'}`}>
                                        {titleLen} / 60 caracteres recomendados
                                    </span>
                                </div>
                                <input
                                    id="meta_title"
                                    type="text"
                                    name="meta_title"
                                    value={form.meta_title}
                                    onChange={handleInputChange}
                                    placeholder="Ej: DXN Tingo María - Café Lingzhi y Ganoderma Oficial"
                                    className="seoInput"
                                />
                                <span className="seoHelperText">
                                    Este es el título azul que los usuarios verán al buscar tu página en Google.
                                </span>
                            </div>

                            {/* Meta Description */}
                            <div className="seoFormGroup">
                                <div className="labelFlex">
                                    <label htmlFor="meta_description">Descripción para Buscadores (Meta Description)</label>
                                    <span className={`charCount ${descLen > 160 ? 'warning' : 'good'}`}>
                                        {descLen} / 155 caracteres recomendados
                                    </span>
                                </div>
                                <textarea
                                    id="meta_description"
                                    name="meta_description"
                                    rows="3"
                                    value={form.meta_description}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Distribuidor autorizado DXN en Tingo María. Encuentra Café Lingzhi, Spirulina y suplementos con Ganoderma Lucidum..."
                                    className="seoTextarea"
                                />
                                <span className="seoHelperText">
                                    Resumen persuasivo que se muestra debajo del título en los resultados de Google.
                                </span>
                            </div>

                            {/* Palabras Clave */}
                            <div className="seoFormGroup">
                                <label htmlFor="meta_keywords">Palabras Clave (Keywords separadas por coma)</label>
                                <input
                                    id="meta_keywords"
                                    type="text"
                                    name="meta_keywords"
                                    value={form.meta_keywords}
                                    onChange={handleInputChange}
                                    placeholder="dxn, tingo maria, ganoderma lucidum, cafe lingzhi 3 en 1"
                                    className="seoInput"
                                />
                                <div className="quickKeywordsRow">
                                    <span className="quickKeywordLabel">Sugerencias rápidas:</span>
                                    {['Ganoderma', 'Café Lingzhi', 'Spirulina Cereal', 'Cocozhi', 'Afiliación DXN Perú', 'Tingo María'].map(tag => (
                                        <button
                                            key={tag}
                                            type="button"
                                            className="tagPillBtn"
                                            onClick={() => agregarKeyword(tag)}
                                        >
                                            + {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Fila de URL Canónica y Marca */}
                            <div className="seoFormRowTwo">
                                <div className="seoFormGroup">
                                    <label htmlFor="site_name">Nombre de Marca / Sitio</label>
                                    <input
                                        id="site_name"
                                        type="text"
                                        name="site_name"
                                        value={form.site_name}
                                        onChange={handleInputChange}
                                        className="seoInput"
                                    />
                                </div>
                                <div className="seoFormGroup">
                                    <label htmlFor="canonical_url">URL Canónica Principal</label>
                                    <input
                                        id="canonical_url"
                                        type="url"
                                        name="canonical_url"
                                        value={form.canonical_url}
                                        onChange={handleInputChange}
                                        placeholder="https://dxntingomaria.com/"
                                        className="seoInput"
                                    />
                                </div>
                            </div>

                            {/* Posicionamiento en Google */}
                            <h3 className="sectionSubtitle mt-4">
                                <FontAwesomeIcon icon={faChartLine} /> Integración con Google
                            </h3>

                            {/* Google Search Console */}
                            <div className="seoFormGroup">
                                <label htmlFor="google_verification">
                                    <i className="fa fa-google" style={{ color: '#ea4335', marginRight: 6 }}></i>
                                    Código de Verificación de Google Search Console
                                </label>
                                <input
                                    id="google_verification"
                                    type="text"
                                    name="google_verification"
                                    value={form.google_verification}
                                    onChange={handleInputChange}
                                    placeholder="Ej: ABcDeF1234567890XYZ o google-site-verification=..."
                                    className="seoInput codeFont"
                                />
                                <span className="seoHelperText">
                                    Permite a Google Search Console verificar la propiedad de tu dominio para indexar tus páginas más rápido.
                                </span>
                            </div>

                            {/* Google Analytics ID */}
                            <div className="seoFormRowTwo">
                                <div className="seoFormGroup">
                                    <label htmlFor="google_analytics_id">ID de Google Analytics 4 (GA4)</label>
                                    <input
                                        id="google_analytics_id"
                                        type="text"
                                        name="google_analytics_id"
                                        value={form.google_analytics_id}
                                        onChange={handleInputChange}
                                        placeholder="Ej: G-XXXXXXXXXX"
                                        className="seoInput codeFont"
                                    />
                                    <span className="seoHelperText">Para medir visitas en tiempo real.</span>
                                </div>

                                <div className="seoFormGroup">
                                    <label htmlFor="robots_index">
                                        <FontAwesomeIcon icon={faRobot} style={{ marginRight: 6 }} />
                                        Indexación (Robots.txt / Meta Robots)
                                    </label>
                                    <select
                                        id="robots_index"
                                        name="robots_index"
                                        value={form.robots_index}
                                        onChange={handleInputChange}
                                        className="seoSelect"
                                    >
                                        <option value="index, follow">index, follow (Recomendado: Indexar y rastrear)</option>
                                        <option value="noindex, follow">noindex, follow (Ocultar de Google)</option>
                                        <option value="index, nofollow">index, nofollow (Indexar sin seguir enlaces)</option>
                                        <option value="noindex, nofollow">noindex, nofollow (Bloquear completamente)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Columna Derecha: Vista Previa en Vivo de Google SERP */}
                        <div className="seoPreviewCard">
                            <div className="previewCardHeader">
                                <div className="previewCardBadge googleBadge">
                                    <i className="fa fa-google"></i> Vista Previa en Google
                                </div>
                                <span className="previewLiveIndicator">● En vivo</span>
                            </div>

                            <p className="previewCardTip">
                                Así se mostrará tu página web cuando los clientes busquen en Google desde su móvil o computadora:
                            </p>

                            {/* Google SERP Snippet Box */}
                            <div className="googleSerpBox">
                                <div className="googleSerpHeader">
                                    <div className="googleFaviconCircle">
                                        <img src="/logo.png" alt="DXN" />
                                    </div>
                                    <div className="googleUrlBreadcrumb">
                                        <span className="googleSiteName">{form.site_name || 'DXN Tingo María'}</span>
                                        <span className="googleUrl">
                                            {form.canonical_url ? form.canonical_url.replace(/^https?:\/\//, '').replace(/\/$/, '') : 'dxntingomaria.com'}
                                        </span>
                                    </div>
                                </div>

                                <div className="googleSerpTitle">
                                    {form.meta_title || 'DXN Tingo María - Salud, Bienestar y Productos con Ganoderma'}
                                </div>

                                <div className="googleSerpSnippet">
                                    {form.meta_description || 'Distribuidor autorizado DXN en Tingo María y todo el Perú. Encuentra Café Lingzhi, Spirulina, Cocozhi y suplementos con Ganoderma Lucidum.'}
                                </div>

                                {/* Rating simulado enriquecido */}
                                <div className="googleRichRating">
                                    <span className="stars">★★★★★</span>
                                    <span className="ratingText">Calificación: 4.9 · 128 opiniones · Distribuidor Oficial</span>
                                </div>
                            </div>

                            {/* Checklist SEO */}
                            <div className="seoChecklist">
                                <h4>
                                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981', marginRight: 6 }} />
                                    Auditoría Rápida de Posicionamiento
                                </h4>
                                <ul className="checklistUl">
                                    <li className={titleLen >= 30 && titleLen <= 65 ? 'checkOk' : 'checkWarn'}>
                                        <span>Longitud de título ({titleLen} caracteres)</span>
                                        <strong>{titleLen >= 30 && titleLen <= 65 ? 'Óptimo' : 'Ajustar'}</strong>
                                    </li>
                                    <li className={descLen >= 70 && descLen <= 165 ? 'checkOk' : 'checkWarn'}>
                                        <span>Longitud de descripción ({descLen} caracteres)</span>
                                        <strong>{descLen >= 70 && descLen <= 165 ? 'Óptimo' : 'Ajustar'}</strong>
                                    </li>
                                    <li className={form.canonical_url ? 'checkOk' : 'checkWarn'}>
                                        <span>URL Canónica configurada</span>
                                        <strong>{form.canonical_url ? 'Correcto' : 'Vacío'}</strong>
                                    </li>
                                    <li className={form.google_verification ? 'checkOk' : 'checkPending'}>
                                        <span>Google Search Console</span>
                                        <strong>{form.google_verification ? 'Vinculado' : 'Pendiente'}</strong>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* ======================================================== */}
                {/* TAB 2: REDES SOCIALES Y OPEN GRAPH (OG IMAGE)            */}
                {/* ======================================================== */}
                {activeTab === 'og' && (
                    <div className="seoTabGrid">
                        {/* Columna Izquierda: Configuración OG & Imagen */}
                        <div className="seoFormCard">
                            <div className="sectionHeaderWithAction">
                                <h3 className="sectionSubtitle" style={{ margin: 0 }}>
                                    <FontAwesomeIcon icon={faShareAlt} /> Etiquetas Open Graph
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleCopiarDeSEO}
                                    className="btnPillOutline"
                                    title="Copiar título y descripción de la pestaña SEO"
                                >
                                    <FontAwesomeIcon icon={faCopy} /> Copiar desde SEO
                                </button>
                            </div>

                            {/* Título Open Graph */}
                            <div className="seoFormGroup mt-3">
                                <div className="labelFlex">
                                    <label htmlFor="og_title">Título para Redes Sociales (og:title)</label>
                                    <span className="charCount">{ogTitleLen} caracteres</span>
                                </div>
                                <input
                                    id="og_title"
                                    type="text"
                                    name="og_title"
                                    value={form.og_title}
                                    onChange={handleInputChange}
                                    placeholder={form.meta_title || "DXN Tingo María | Salud y Prosperidad"}
                                    className="seoInput"
                                />
                            </div>

                            {/* Descripción Open Graph */}
                            <div className="seoFormGroup">
                                <div className="labelFlex">
                                    <label htmlFor="og_description">Descripción para Redes Sociales (og:description)</label>
                                    <span className="charCount">{ogDescLen} caracteres</span>
                                </div>
                                <textarea
                                    id="og_description"
                                    name="og_description"
                                    rows="3"
                                    value={form.og_description}
                                    onChange={handleInputChange}
                                    placeholder={form.meta_description || "Descubre los productos con Ganoderma Lucidum..."}
                                    className="seoTextarea"
                                />
                            </div>

                            {/* Subida de Imagen OG */}
                            <div className="seoFormGroup">
                                <label>
                                    <FontAwesomeIcon icon={faImage} style={{ marginRight: 6 }} />
                                    Imagen para Compartir en Redes Sociales (OG Image) *
                                </label>

                                <div className="ogUploadArea">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept="image/png, image/jpeg, image/webp"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        id="ogImageFileInput"
                                    />

                                    <div className="uploadTriggerBox" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                                        <FontAwesomeIcon icon={faUpload} className="uploadIconLarge" />
                                        <div className="uploadTexts">
                                            <strong>Haz clic aquí para seleccionar o arrastrar una imagen</strong>
                                            <span>Formatos permitidos: JPG, PNG, WEBP · Resolución recomendada: 1200 x 630 px</span>
                                        </div>
                                    </div>

                                    {/* Opción de URL directa */}
                                    <div className="ogUrlInputRow">
                                        <label htmlFor="og_image" style={{ fontSize: '0.85rem' }}>O pega un enlace de imagen externo:</label>
                                        <input
                                            id="og_image"
                                            type="url"
                                            name="og_image"
                                            value={form.og_image}
                                            onChange={handleInputChange}
                                            placeholder="https://tudominio.com/imagenes/mi-portada-og.jpg"
                                            className="seoInput"
                                        />
                                    </div>

                                    {/* Previsualización de la imagen actual */}
                                    {imagenMostrada && (
                                        <div className="currentOgImageBanner">
                                            <img src={imagenMostrada} alt="Vista previa OG" />
                                            <div className="imageOverlayActions">
                                                <span className="badgeDim">1200 x 630 (1.91:1)</span>
                                                <button
                                                    type="button"
                                                    onClick={handleQuitarImagen}
                                                    className="btnRemoveImg"
                                                    title="Quitar imagen"
                                                >
                                                    Quitar Imagen
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Parámetros de Twitter / X */}
                            <h3 className="sectionSubtitle mt-4">
                                <i className="fa fa-twitter"></i> Parámetros de Twitter / X
                            </h3>

                            <div className="seoFormRowTwo">
                                <div className="seoFormGroup">
                                    <label htmlFor="twitter_card">Tipo de Tarjeta (Twitter Card)</label>
                                    <select
                                        id="twitter_card"
                                        name="twitter_card"
                                        value={form.twitter_card}
                                        onChange={handleInputChange}
                                        className="seoSelect"
                                    >
                                        <option value="summary_large_image">summary_large_image (Foto Grande - Recomendado)</option>
                                        <option value="summary">summary (Tarjeta Pequeña)</option>
                                    </select>
                                </div>

                                <div className="seoFormGroup">
                                    <label htmlFor="twitter_site">Usuario de Twitter / X</label>
                                    <input
                                        id="twitter_site"
                                        type="text"
                                        name="twitter_site"
                                        value={form.twitter_site}
                                        onChange={handleInputChange}
                                        placeholder="@dxntingomaria"
                                        className="seoInput"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Columna Derecha: Vista Previa en Redes Sociales */}
                        <div className="seoPreviewCard">
                            <div className="previewCardHeader">
                                <div className="previewCardBadge socialBadge">
                                    <FontAwesomeIcon icon={faShareAlt} /> Vista Previa en Redes
                                </div>

                                {/* Selector de vista previa (WhatsApp / Twitter) */}
                                <div className="socialToggleBtns">
                                    <button
                                        type="button"
                                        className={`toggleSocialBtn ${socialPreviewMode === 'whatsapp' ? 'active' : ''}`}
                                        onClick={() => setSocialPreviewMode('whatsapp')}
                                    >
                                        <i className="fa fa-whatsapp"></i> WhatsApp / FB
                                    </button>
                                    <button
                                        type="button"
                                        className={`toggleSocialBtn ${socialPreviewMode === 'twitter' ? 'active' : ''}`}
                                        onClick={() => setSocialPreviewMode('twitter')}
                                    >
                                        <i className="fa fa-twitter"></i> Twitter / X
                                    </button>
                                </div>
                            </div>

                            {/* Vista Previa de WhatsApp / Facebook */}
                            {socialPreviewMode === 'whatsapp' ? (
                                <div className="whatsappChatPreviewBox">
                                    <div className="whatsappChatBubble">
                                        <div className="socialCardContainer">
                                            <div className="socialCardImageWrapper">
                                                <img
                                                    src={imagenMostrada}
                                                    alt="Open Graph preview"
                                                    className="socialCardImg"
                                                    onError={(e) => { e.target.src = '/logo.png'; }}
                                                />
                                            </div>
                                            <div className="socialCardContent">
                                                <span className="socialCardDomain">
                                                    {form.canonical_url ? form.canonical_url.replace(/^https?:\/\//, '').replace(/\/$/, '').toUpperCase() : 'DXNTINGOMARIA.COM'}
                                                </span>
                                                <h4 className="socialCardTitle">
                                                    {form.og_title || form.meta_title || 'DXN Tingo María - Productos Naturales'}
                                                </h4>
                                                <p className="socialCardDesc">
                                                    {form.og_description || form.meta_description || 'Distribuidor autorizado DXN en Tingo María y todo el Perú. ¡Afíliate y compra con descuento!'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="bubbleFooterTime">10:45 AM ✓✓</div>
                                    </div>
                                    <span className="previewFootnote">
                                        Así verán tus clientes la tarjeta cuando compartas el enlace por WhatsApp, grupos o chats privados.
                                    </span>
                                </div>
                            ) : (
                                /* Vista Previa de Twitter / X */
                                <div className="twitterCardPreviewBox">
                                    <div className="twitterTweetMock">
                                        <div className="tweetAuthorRow">
                                            <img src="/logo.png" alt="DXN Avatar" className="tweetAvatar" />
                                            <div>
                                                <strong>{form.site_name || 'DXN Tingo María'}</strong>
                                                <span className="tweetHandle">{form.twitter_site || '@dxntingomaria'} · Ahora</span>
                                            </div>
                                        </div>

                                        <p className="tweetText">¡Visita nuestra tienda oficial en línea de DXN Tingo María! 🌱☕</p>

                                        <div className="twitterMediaCard">
                                            <div className="twitterCardImgWrap">
                                                <img
                                                    src={imagenMostrada}
                                                    alt="Twitter preview"
                                                    className="twitterCardImg"
                                                    onError={(e) => { e.target.src = '/logo.png'; }}
                                                />
                                            </div>
                                            <div className="twitterCardBody">
                                                <span className="twitterCardDomain">
                                                    {form.canonical_url ? form.canonical_url.replace(/^https?:\/\//, '').replace(/\/$/, '') : 'dxntingomaria.com'}
                                                </span>
                                                <h5 className="twitterCardTitle">
                                                    {form.og_title || form.meta_title || 'DXN Tingo María'}
                                                </h5>
                                                <p className="twitterCardSnippet">
                                                    {form.og_description || form.meta_description || 'Café Lingzhi, Spirulina y Ganoderma Lucidum.'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <span className="previewFootnote">
                                        Simulación de la tarjeta con imagen grande en Twitter / X y redes asociadas.
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ======================================================== */}
                {/* TAB 3: CÓDIGO HTML & DIAGNÓSTICO                        */}
                {/* ======================================================== */}
                {activeTab === 'code' && (
                    <div className="seoTabGridSingle">
                        <div className="seoFormCard">
                            <div className="sectionHeaderWithAction">
                                <div>
                                    <h3 className="sectionSubtitle" style={{ margin: 0 }}>
                                        <FontAwesomeIcon icon={faCode} /> Etiquetas HTML Generadas en Tiempo Real
                                    </h3>
                                    <p className="sectionHint">
                                        Estas son las etiquetas estándar que se inyectan automáticamente en el encabezado (`&lt;head&gt;`) de tu sitio para Google y redes sociales.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={copiarCodigoPortapapeles}
                                    className="btnPrimarySeo"
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                    <span>Copiar Código</span>
                                </button>
                            </div>

                            <div className="codeViewerWrapper">
                                <pre className="codePreBlock">
                                    <code>{codigoGenerado}</code>
                                </pre>
                            </div>

                            <div className="seoGuidelinesBox">
                                <h4>💡 Recomendaciones de Oro para el Posicionamiento de DXN Tingo María</h4>
                                <div className="guidelinesGrid">
                                    <div className="guidelineItem">
                                        <strong>1. Título Único y Atractivo:</strong>
                                        <p>Incluye siempre la palabra clave principal al inicio (p. ej. "DXN Tingo María") seguida del beneficio o producto estrella ("Café Lingzhi & Ganoderma").</p>
                                    </div>
                                    <div className="guidelineItem">
                                        <strong>2. Imagen Open Graph en alta definición:</strong>
                                        <p>Usa una imagen llamativa de 1200x630 píxeles. Evita texto excesivo dentro de la foto para que se vea nítida en pantallas pequeñas de smartphones.</p>
                                    </div>
                                    <div className="guidelineItem">
                                        <strong>3. Google Search Console:</strong>
                                        <p>Pega tu código de verificación para que Google indexe de inmediato todas las categorías y productos de tu tienda.</p>
                                    </div>
                                    <div className="guidelineItem">
                                        <strong>4. Llamado a la Acción (CTA):</strong>
                                        <p>En la meta descripción y en Open Graph, incluye siempre verbos de acción como "¡Compra ahora!", "¡Afíliate hoy!" o "Pide por WhatsApp".</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
